import type { RequestOptions } from '@@/plugin-request/request';
import type { RequestConfig } from '@umijs/max';
import { history } from '@umijs/max';
import { message, notification } from 'antd';

// 登录页路径
const loginPath = '/user/login';

// token 存储键
const TOKEN_KEY = 'admin_token';

/**
 * 统一响应结构（由后端 ResponseWrapperMiddleware 包装）
 */
interface StandardApiResponse<T = unknown> {
  code: number;
  message: string;
  data: T;
}

/**
 * 判断是否为对象（排除 null 与基础类型）
 */
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/**
 * 判断是否为后端统一响应结构
 */
function isStandardApiResponse(value: unknown): value is StandardApiResponse {
  if (!isRecord(value)) return false;
  return (
    typeof value.code === 'number'
    && typeof value.message === 'string'
    && 'data' in value
  );
}

/**
 * 获取存储的 admin token
 */
export function getToken(): string | null {
  const token = localStorage.getItem(TOKEN_KEY);
  // 防御式处理：避免把字符串 "undefined"/"null" 当成有效 token 使用
  if (!token || token === 'undefined' || token === 'null') {
    if (token) {
      localStorage.removeItem(TOKEN_KEY);
    }
    return null;
  }
  return token;
}

/**
 * 存储 admin token
 */
export function setToken(token: string): void {
  // 兜底校验：避免把空串或非法字符串落盘，导致后续请求带上无效 Authorization
  if (!token || token === 'undefined' || token === 'null') {
    clearToken();
    return;
  }
  localStorage.setItem(TOKEN_KEY, token);
}

/**
 * 清除 admin token
 */
export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

/**
 * 请求配置：错误处理 + 拦截器
 */
export const errorConfig: RequestConfig = {
  // 响应拦截器：自动解包后端统一响应格式，业务层统一拿到 data
  responseInterceptors: [
    (response) => {
      const payload = response?.data;
      if (!isStandardApiResponse(payload)) {
        return response;
      }
      // code=0 视为成功，直接将真正业务数据提升为 response.data
      if (payload.code === 0) {
        (response as any).data = payload.data;
      }
      return response;
    },
  ],

  errorConfig: {
    errorThrower: (res: any) => {
      // FastAPI 直接返回错误对象，不做 pro 默认的 success 判断
    },
    errorHandler: (error: any, opts: any) => {
      if (opts?.skipErrorHandler) throw error;

      if (error.response) {
        const status = error.response.status;
        if (status === 401) {
          // token 无效或过期，跳转登录页
          clearToken();
          message.error('登录已过期，请重新登录');
          history.push(loginPath);
        } else if (status === 403) {
          message.error('无权限访问');
        } else if (status === 404) {
          message.error('请求资源不存在');
        } else {
          const detail = error.response.data?.detail;
          message.error(detail || `请求错误 (${status})`);
        }
      } else if (error.request) {
        message.error('网络异常，请检查网络连接');
      } else {
        message.error('请求发送失败');
      }
    },
  },

  // 请求拦截器：注入 admin token
  requestInterceptors: [
    (config: RequestOptions) => {
      const token = getToken();
      if (token) {
        // 注意：首次请求时 config.headers 可能为 undefined，需兜底创建后再注入 token
        config.headers = {
          ...(config.headers || {}),
          Authorization: `Bearer ${token}`,
        };
      }
      return config;
    },
  ],
};
